import { FC, PropsWithChildren, useLayoutEffect, useRef } from 'react'
import ReactDataSheet from 'react-datasheet'
import { Button } from '@progress/kendo-react-buttons'
import { Checkbox, Input } from '@progress/kendo-react-inputs'

import { WorkQuantity } from '@gateway.Supplier'
import {
  CellValue,
  GridDataSate,
  GridElement
} from './QuotationRequestBuilding'

import 'react-datasheet/lib/react-datasheet.css'

import hearingMessage from '../../../data/hearing-message.json'
import {
  ConfirmReplyActions, useAppDispatch,
  useAppSelector
} from '../../../stores'
import { CodeName } from '../../../services/initializing/CodeName'
import { Tooltip } from '@progress/kendo-react-tooltip'

/** メインのグリッド設定 */
const SheetRenderer: FC<
PropsWithChildren<{
  className: string
  simulationYm: string
  years: number
  months: number[]
  displayStartYear: string
}>
> = ({ className, simulationYm, years, months, children, displayStartYear }) => {
  const ref = useRef<HTMLDivElement>(null)
  const location = useAppSelector((state) => state.confirmreply.location.supplierDecisionBuildingLocation)
  const locationTop = useAppSelector((state) => state.confirmreply.locationTop.supplierEstimationBuildingLocation)
  const dispatch = useAppDispatch()

  const createYearColumns = (startYear: string) => {
    const firstYear = Number(startYear)
    const emptyArray = Array.from({ length: years })

    return emptyArray.map<number>((_value, index) => {
      return firstYear + index
    })
  }

  useLayoutEffect(() => {
    ref.current?.scrollTo({ left: location, top: locationTop })
  }, [location, locationTop])

  return (
    <div
      ref={ref}
      className="scrollable-container"
      style={{ maxHeight: '62vh' }}
      onScroll={(event) => {
        dispatch(ConfirmReplyActions.setSupplierDecisionBuildingLocation(Number(ref.current?.scrollLeft)))
        dispatch(ConfirmReplyActions.setSupplierEstimationBuildingLocationTop(Number(ref.current?.scrollTop)))
      }}
      >
      <table
        className={className}
        style={{ borderCollapse: 'separate', borderSpacing: 0 }}
      >
        <thead>
          <tr>
            <th rowSpan={2} style={{ position: 'sticky', left: 0 }}>
              <div style={{ width: 30 }}>No</div>
            </th>
            <th rowSpan={2} style={{ position: 'sticky', left: 33 }}>
              <div style={{ width: 40 }}>非表示</div>
            </th>
            <th rowSpan={2} style={{ position: 'sticky', left: 76 }}>
              <div style={{ width: 60 }}>詳細</div>
            </th>
            <th rowSpan={2} style={{ position: 'sticky', left: 139 }}>
              <div style={{ width: 60 }}>管轄支店</div>
            </th>
            <th rowSpan={2} style={{ position: 'sticky', left: 202 }}>
              <div style={{ width: 55 }}>手持区分</div>
            </th>
            <th rowSpan={2} style={{ position: 'sticky', left: 260 }}>
              <div style={{ width: 270 }}>工事名称</div>
            </th>
            <th rowSpan={2} style={{ position: 'sticky', left: 533 }}>
              <div style={{ width: 440 }}>備考</div>
            </th>
            {createYearColumns(displayStartYear).map((year) => (
              <th colSpan={12} key={year}>
                {year}年度
              </th>
            ))}
          </tr>
          <tr>
            {months.map((m) => (
              <th key={m} className="calendar-month">
                {m}
              </th>
            ))}
            {months.map((m) => (
              <th key={m} className="calendar-month">
                {m}
              </th>
            ))}
            {months.map((m) => (
              <th key={m} className="calendar-month">
                {m}
              </th>
            ))}
            {months.map((m) => (
              <th key={m} className="calendar-month">
                {m}
              </th>
            ))}
            {months.map((m) => (
              <th key={m} className="calendar-month">
                {m}
              </th>
            ))}
            {months.map((m) => (
              <th key={m} className="calendar-month">
                {m}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  )
}

/** 左ヘッダの設定 */
const RowRenderer: FC<
PropsWithChildren<{
  displaySelected: boolean
  display: string
  rowNo: number
  rowLength: number
  workQuantity: WorkQuantity
  nonCtrlFlg: boolean
  completedIndexList: number[]
  userAuthority: number
  onWorkQuantityBtnClick: (workQuantity: WorkQuantity) => void
  onDisplayCheckboxChange: (
    selectedRowIndex: number,
    selected: boolean
  ) => void
  onRemarkInputChange: (val: string, rowNo: number) => void
}>
> = ({
  displaySelected,
  display,
  rowNo,
  rowLength,
  workQuantity,
  nonCtrlFlg,
  completedIndexList,
  userAuthority,
  onWorkQuantityBtnClick,
  onDisplayCheckboxChange,
  onRemarkInputChange,
  // right part
  children
}) => {
  // ログイン情報．権限区分＝"5"(業者)の場合、M1手持区分とM1非表示を非表示します。
  const authorityDivisionHidden = userAuthority === 5

  // M1明細の各行に対し、State．新規追加フラグ=1(新規)の場合は赤字で表示する
  const isRed =
    workQuantity !== undefined ? workQuantity.newAddFlg === 1 : false

  const isLastRow = rowNo === rowLength - 1

  // 支店情報のリストを取得
  const initialData = useAppSelector((x) => x.common.initialData)
  const branches = initialData.codeNames('SI01')

  // 竣工済物件の分を詰めた行番号の算出
  const no = (rowNo + 1) - completedIndexList.filter(i => i < rowNo).length

  if (isLastRow) {
    return (
      <tr style={{ height: 33 }}>
        <th
          style={{ position: 'sticky', left: 0, textAlign: 'left' }}
          colSpan={7}
        >
          <span>合計</span>
        </th>
        {children}
      </tr>
    )
  }

  if (workQuantity.completedFlg === 1) {
    return (<></>)
  }

  return (
    <tr style={{ height: 33, display: `${display}` }}>
      <th style={{ position: 'sticky', left: 0, color: isRed ? '#F00' : '' }}>
        <span>{no}</span>
      </th>
      <th style={{ position: 'sticky', left: 33 }}>
        {!authorityDivisionHidden && (
          <Checkbox
            style={{ color: isRed ? '#F00' : '' }}
            checked={displaySelected}
            disabled={workQuantity.lostOrderFlg === 1 || nonCtrlFlg}
            onChange={(e) => onDisplayCheckboxChange(rowNo, e.value)}
          />
        )}
      </th>
      <th style={{ position: 'sticky', left: 76 }}>
        <Button
          style={{ color: isRed ? '#F00' : '' }}
          onClick={() => {
            onWorkQuantityBtnClick(workQuantity)
          }}
          disabled={workQuantity.buildingDiscloseFlg === 0}
        >
          詳細
        </Button>
      </th>
      <th style={{ position: 'sticky', left: 139, color: isRed ? '#F00' : '' }}>
        <span>
        {
          branches.find((item: CodeName) => {
            return item.CODE === workQuantity.branchCd
          })?.NAME
        }
        </span>
      </th>
      <th style={{ position: 'sticky', left: 202, color: isRed ? '#F00' : '' }}>
        <span>{!authorityDivisionHidden && workQuantity.evaluation}</span>
      </th>
      <th style={{ position: 'sticky', left: 260, color: isRed ? '#F00' : '' }}>
        <Tooltip position='right'>
              <span title={workQuantity.constructionNm ?? ''} style={{ color: isRed ? '#F00' : '', width: 270, display: 'block', textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}>{workQuantity.constructionNm}</span>
        </Tooltip>
      </th>
      {
        workQuantity.lostOrderFlg === 1 || nonCtrlFlg
          ? (
          <>
            <th style={{ position: 'sticky', left: 533, color: isRed ? '#F00' : '' }}>
              <Tooltip position='right'>
                    <span title={workQuantity.remark ?? ''} style={{ color: isRed ? '#F00' : '', width: 440, display: 'block', textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}>{workQuantity.remark}</span>
              </Tooltip>
            </th>
          </>
            )
          : (
          <>
            <th style={{ position: 'sticky', left: 533 }}>
              <Input
                defaultValue={workQuantity.remark ?? ''}
                style={{ color: isRed ? '#F00' : '' }}
                onBlur={(e) => {
                  onRemarkInputChange(e.target.value, rowNo)
                }}
              />
            </th>
          </>
            )
      }
      {children}
    </tr>
  )
}

/** セルの設定 */
const ValueViewer: FC<{
  valueViewerProps: ReactDataSheet.ValueViewerProps<GridElement, number>
  rowLength: number
  isRed: boolean
}> = ({ valueViewerProps, rowLength, isRed }) => {
  const isLastRow = valueViewerProps.row === rowLength - 1

  return (
    <div
      style={{
        backgroundColor: isLastRow ? '#99FF99' : '',
        height: '100%',
        color: isRed ? '#F00' : ''
      }}
    >
      {valueViewerProps.value}
    </div>
  )
}

interface QuotationRequestBuildingGridProps {
  gridDataState: GridDataSate
  years: number
  months: number[]
  simulationYm: string
  displayStartYear: string
  nonCtrlFlg: boolean
  userAuthority: number
  onDisplayCheckboxChange: (selectedRowIndex: number, selected: boolean) => void
  onWorkQuantityBtnClick: (workQuantity: WorkQuantity) => void
  expenseCd: string
  onCellsChanged: (
    arrayOfChanges: ReactDataSheet.CellsChangedArgs<GridElement, CellValue>
  ) => void
  onRemarkInputChange: (val: string, rowNo: number) => void
}

export const QuotationRequestBuildingGrid: FC<QuotationRequestBuildingGridProps> = (props) => {
  /** Table Header */
  const initialData = useAppSelector((x) => x.common.initialData)
  const expenses = initialData.codeNames('HI01')
  const unit = expenses.find((item: CodeName) => {
    return item.CODE === props.expenseCd
  })?.TXT8
  const sheetRenderer = (
    sheetRendererProps: ReactDataSheet.SheetRendererProps< GridElement, CellValue>
  ) => {
    return (
      <SheetRenderer
        className={sheetRendererProps.className}
        simulationYm={props.simulationYm}
        years={props.years}
        months={props.months}
        displayStartYear={props.displayStartYear}
      >
        {sheetRendererProps.children}
      </SheetRenderer>
    )
  }

  /** Table Row[Lift + Right(→rowRendererProps.children)] */
  const rowRenderer = (
    rowRendererProps: ReactDataSheet.RowRendererProps<
    GridElement,
    CellValue
    > & { selected: boolean }
  ) => {
    const workQuantity: WorkQuantity =
      props.gridDataState.workQuantityList[rowRendererProps.row]
    const currentRowDisplaySelection =
      workQuantity !== undefined ? workQuantity.hiddenFlg !== 0 : false

    const display = props.gridDataState.displayModes[rowRendererProps.row]
      ? ''
      : 'none'
    // 竣工済物件のインデックス番号
    const completedIndexList = props.gridDataState.workQuantityList.flatMap((w, i) => (w.completedFlg === 1 ? i : []))

    return (
      <RowRenderer
        displaySelected={currentRowDisplaySelection}
        display={display}
        onDisplayCheckboxChange={props.onDisplayCheckboxChange}
        rowNo={rowRendererProps.row}
        rowLength={props.gridDataState.grid.length}
        workQuantity={workQuantity}
        nonCtrlFlg={props.nonCtrlFlg}
        completedIndexList={completedIndexList}
        userAuthority={props.userAuthority}
        onWorkQuantityBtnClick={props.onWorkQuantityBtnClick}
        onRemarkInputChange={props.onRemarkInputChange}
      >
        {rowRendererProps.children}
      </RowRenderer>
    )
  }

  const valueViewer = (
    valueViewerProps: ReactDataSheet.ValueViewerProps<GridElement, number>
  ) => {
    const workQuantity: WorkQuantity =
      props.gridDataState.workQuantityList[valueViewerProps.row]

    let isRed = false

    if (workQuantity !== undefined) {
      if (workQuantity.newAddFlg === 1) {
        // M1明細の各行に対し、State．新規追加フラグ=1(新規)の場合は赤字で表示する
        isRed = true
      } else {
        const colNo = valueViewerProps.col
        const workQuantityListLastMonth = props.gridDataState.workQuantityListLastMonth
        const lastMonth = workQuantityListLastMonth.find((obj) =>
          obj.procurementNo === workQuantity.procurementNo && obj.subNo === workQuantity.subNo && obj.expenseSeq === workQuantity.expenseSeq)

        // ※対象の支店コードに一致する変数．前月度労務能力．見積依頼物件．月別労務数が取得出来る場合
        if (workQuantity !== undefined && lastMonth !== undefined) {
          const simulatedMonth = Number(props.simulationYm.substring(4, props.simulationYm.length))

          // ※データストア．業者ヒアリング遷移パラメータ．パラメータ．シミュレーション年月度が4月度の場合、
          // 変数．前月度労務能力．見積依頼物件．月別労務数のIndexを+12する
          if (simulatedMonth === 4) {
            if (colNo + 12 <= workQuantity.workQuantityByMonth.length - 1) {
              isRed = workQuantity.workQuantityByMonth[colNo] !== lastMonth?.workQuantityByMonth[colNo + 12]
            } else {
              isRed = true
            }
          } else {
            isRed = workQuantity.workQuantityByMonth[colNo] !== lastMonth?.workQuantityByMonth[colNo]
          }
        }
      }
    }

    return (
      <ValueViewer
        isRed={isRed}
        valueViewerProps={valueViewerProps}
        rowLength={props.gridDataState.grid.length}
      />
    )
  }

  return (
    <div style={{ position: 'sticky', zIndex: 3 }}>
      <h3 style={{ marginBottom: 0 }}>
        大成見積中物件（単位：{unit}）
        <span
          style={{ marginLeft: '16px', color: '#d51923', fontWeight: '700', fontSize: '16px' }}
        >
          {hearingMessage.quotationRequestBuilding.quotationRequestBuildingAnnotationInfo}
        </span>
      </h3>
      <ReactDataSheet<GridElement, CellValue>
        data={props.gridDataState.grid}
        sheetRenderer={sheetRenderer}
        rowRenderer={rowRenderer}
        valueRenderer={(cell) => cell.value}
        valueViewer={valueViewer}
        onCellsChanged={props.onCellsChanged}
      />
    </div>
  )
}
